import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { AccordionSummary, Box, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { GenericBarChart } from '@/components/shared/GenericBarChart';
import { AccordionStyle, scrollDesign } from '../styles';
import { GraphsLoader } from '../PaintBlock/Summary/GraphsLoader';
import { useFindingsData } from '../PaintBlock/Summary/hooks/useFindingsData';
import { useDefectsGraphData } from '../PaintBlock/Summary/hooks/useDefectsGraphData';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  selectedFilteredDeckIds,
  selectedFilteredPaintRegionIds,
  selectedTemplateFiltersState,
} from '@/components/Analysis/state';
import { useAtomValue } from 'jotai';
import { useDefectsAreaAndPointOfInterestCountByPaintRegionsQuery } from '@/__generated__/graphql';
import {
  PaintBlocksCorrodedArea,
  PointOfInterestsFindingsByDeckAndPaintBlock,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { Label } from 'recharts';
import { useDefectLegendsForPaintBlockTableLazyQuery } from '@/__generated__/graphql';
import {
  useActiveCoatingConditionLegends,
  useTemplateFieldNames,
} from '../PaintBlock/Summary/hooks/useDefectsUtils';
import { DefectLegend } from '../PaintBlock/PaintBlockContainer';
import { abyssColors } from '@/theme/colors';

const DecksGraphBase = () => {
  const unitSystem = useAtomValue(state.unitSystem);
  const structureId = useGetStructureId();
  const selectedTemplateFilters = useAtomValue(selectedTemplateFiltersState);
  const filteredDeckIds = useAtomValue(selectedFilteredDeckIds);
  const paintRegionIds = useAtomValue(selectedFilteredPaintRegionIds);
  const graphsContainerReference = useRef<HTMLDivElement>(null);
  const [graphsContainerWidth, setGraphsContainerWidth] = useState<number>(0);
  const [defectsData, setDefectsData] = useState<PaintBlocksCorrodedArea | undefined>(undefined);
  const [findingsData, setFindingsData] = useState<
    PointOfInterestsFindingsByDeckAndPaintBlock | undefined
  >(undefined);
  const [defectLegend, setDefectLegend] = useState<DefectLegend[] | undefined>([]);

  const { loading: combinedLoading } = useDefectsAreaAndPointOfInterestCountByPaintRegionsQuery({
    variables: {
      input: {
        structureIds: [structureId],
        selectedTemplateFilters,
        deckIds: [...new Set(filteredDeckIds)],
        paintRegionIds,
      },
    },
    onCompleted(data) {
      if (data) {
        setDefectsData(data.defectsAreaForAssemblyPaintRegions);
        setFindingsData(data.pointOfInterestCountByPaintRegions);
      }
    },
  });

  const templateFieldNames = useTemplateFieldNames(defectLegend);
  const activeCoatingConditionLegends = useActiveCoatingConditionLegends(
    defectLegend,
    templateFieldNames
  );

  const { chartsData: processedDefectsData } = useDefectsGraphData(defectsData, templateFieldNames);

  const [fetchDefectLegends] = useDefectLegendsForPaintBlockTableLazyQuery();
  useEffect(() => {
    fetchDefectLegends({
      variables: {
        structureId,
      },
      fetchPolicy: 'cache-first',
    }).then(({ data }) => {
      const defects = data?.structure?.defectLegends;
      setDefectLegend(defects);
    });
  }, [fetchDefectLegends, structureId]);

  const { chartsData: processedFindingsData, findingsLegend } = useFindingsData(findingsData, []);

  useEffect(() => {
    if (graphsContainerReference.current) {
      const container = graphsContainerReference.current;
      const resizeObserver = new ResizeObserver(() => {
        setGraphsContainerWidth(container.clientWidth);
      });
      resizeObserver.observe(container);
      return () => {
        resizeObserver.unobserve(container);
      };
    }
    return undefined;
  }, []);

  const labelValue = useMemo(() => {
    return (
      <Label
        offset={20}
        position="top"
        style={{
          fontSize: '1.1rem',
          fill: abyssColors.primary[400],
        }}
        opacity={0.8}
        value={unitSystem === UnitSystemEnum.Imperial ? '(ft²)' : '(m²)'}
      />
    );
  }, [unitSystem]);
  return (
    <AccordionStyle disableGutters defaultExpanded elevation={0}>
      <AccordionSummary sx={{ padding: '0px' }} expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 500, fontSize: '1.8rem' }}>Findings By Decks</Typography>
      </AccordionSummary>
      <Box ref={graphsContainerReference}>
        {combinedLoading && <GraphsLoader height="383px" />}

        <Stack direction="row" spacing={2} mb={3}>
          <Box sx={{ width: '50%', ...scrollDesign }}>
            <GenericBarChart
              title="Coating condition by deck"
              data={processedDefectsData.defectsByDeck}
              legendData={activeCoatingConditionLegends}
              containerWidth={graphsContainerWidth}
              customLabel={labelValue}
            />
          </Box>
          <Box sx={{ width: '50%', ...scrollDesign }}>
            <GenericBarChart
              title="Finding count by deck"
              data={processedFindingsData.findingsCountByDeck}
              legendData={findingsLegend}
              containerWidth={graphsContainerWidth}
            />
          </Box>
        </Stack>
      </Box>
    </AccordionStyle>
  );
};

export const DecksGraph = memo(DecksGraphBase);
